import { rrulestr } from 'rrule'
import { format, parseISO, startOfDay } from 'date-fns'
import type { Task } from '@/types'

/**
 * Generate occurrences of a recurring task
 * @param task - The task with recurrence rule
 * @param startDate - Start date for occurrence generation
 * @param endDate - End date for occurrence generation
 * @returns Array of occurrence dates
 */
export function generateTaskOccurrences(
  task: Task,
  startDate: Date,
  endDate: Date
): Date[] {
  if (!task.recurrence_rule) {
    // One-time task
    const taskDate = parseISO(task.start_date)
    if (taskDate >= startDate && taskDate <= endDate) {
      return [taskDate]
    }
    return []
  }

  try {
    // Parse the RRule
    const rule = rrulestr(task.recurrence_rule, {
      dtstart: parseISO(task.start_date),
    })

    // Generate occurrences
    let occurrences = rule.between(startDate, endDate, true)

    // Filter out skip dates
    if (task.skip_dates && task.skip_dates.length > 0) {
      const skipDates = task.skip_dates.map((d) => format(parseISO(d), 'yyyy-MM-dd'))
      occurrences = occurrences.filter((date) => {
        const dateStr = format(date, 'yyyy-MM-dd')
        return !skipDates.includes(dateStr)
      })
    }

    // Respect task end_date if set
    if (task.end_date) {
      const taskEndDate = parseISO(task.end_date)
      occurrences = occurrences.filter((date) => date <= taskEndDate)
    }

    return occurrences
  } catch (error) {
    console.error('Error generating task occurrences:', error)
    return []
  }
}

/**
 * Get the next occurrence of a recurring task
 * @param task - The task with recurrence rule
 * @param after - Date to look after (defaults to now)
 * @returns Next occurrence date or null
 */
export function getNextOccurrence(task: Task, after?: Date): Date | null {
  const afterDate = after || new Date()

  if (!task.recurrence_rule) {
    const taskDate = parseISO(task.start_date)
    return taskDate > afterDate ? taskDate : null
  }

  try {
    const rule = rrulestr(task.recurrence_rule, {
      dtstart: parseISO(task.start_date),
    })

    const next = rule.after(afterDate, true)

    if (!next) return null

    // Check if it's in skip dates
    if (task.skip_dates && task.skip_dates.length > 0) {
      const skipDates = task.skip_dates.map((d) => format(parseISO(d), 'yyyy-MM-dd'))
      const nextStr = format(next, 'yyyy-MM-dd')

      if (skipDates.includes(nextStr)) {
        // Recursively find next non-skipped occurrence
        return getNextOccurrence(task, next)
      }
    }

    // Check against task end_date
    if (task.end_date && next > parseISO(task.end_date)) {
      return null
    }

    return next
  } catch (error) {
    console.error('Error getting next occurrence:', error)
    return null
  }
}

/**
 * Check if a task is due on a specific date
 * @param task - The task to check
 * @param date - The date to check
 * @returns True if task is due on this date
 */
export function isTaskDueOnDate(task: Task, date: Date): boolean {
  const checkDate = startOfDay(date)

  if (!task.recurrence_rule) {
    const taskDate = startOfDay(parseISO(task.start_date))
    return taskDate.getTime() === checkDate.getTime()
  }

  try {
    const rule = rrulestr(task.recurrence_rule, {
      dtstart: parseISO(task.start_date),
    })

    const occurrences = rule.between(checkDate, new Date(checkDate.getTime() + 86400000), true)

    if (occurrences.length === 0) return false

    // Check skip dates
    if (task.skip_dates && task.skip_dates.length > 0) {
      const dateStr = format(checkDate, 'yyyy-MM-dd')
      if (task.skip_dates.includes(dateStr)) {
        return false
      }
    }

    // Check task end_date
    if (task.end_date && checkDate > parseISO(task.end_date)) {
      return false
    }

    return true
  } catch (error) {
    console.error('Error checking task due date:', error)
    return false
  }
}

/**
 * Create an RRule string from simple parameters
 * @param frequency - DAILY, WEEKLY, MONTHLY, YEARLY
 * @param interval - Every N periods (default 1)
 * @param byweekday - Day of week for weekly tasks (0=Monday, 6=Sunday)
 * @param bymonthday - Day of month for monthly tasks
 * @param bysetpos - Position in month (1=first, -1=last)
 * @returns RRule string
 */
export function createRRule(params: {
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
  interval?: number
  byweekday?: number[]
  bymonthday?: number
  bysetpos?: number
}): string {
  const parts = [`FREQ=${params.frequency}`]

  if (params.interval && params.interval > 1) {
    parts.push(`INTERVAL=${params.interval}`)
  }

  if (params.byweekday && params.byweekday.length > 0) {
    const days = params.byweekday.map((d) => {
      const dayNames = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
      return dayNames[d]
    })
    parts.push(`BYDAY=${days.join(',')}`)
  }

  if (params.bymonthday) {
    parts.push(`BYMONTHDAY=${params.bymonthday}`)
  }

  if (params.bysetpos) {
    parts.push(`BYSETPOS=${params.bysetpos}`)
  }

  return parts.join(';')
}

/**
 * Parse a human-readable description from RRule string
 * @param rrule - RRule string
 * @returns Human-readable description
 */
export function getRRuleDescription(rrule: string): string {
  try {
    const rule = rrulestr(rrule)
    return rule.toText()
  } catch (error) {
    return 'Invalid recurrence rule'
  }
}

/**
 * Common recurrence presets
 */
export const RECURRENCE_PRESETS = {
  DAILY: 'FREQ=DAILY',
  WEEKLY: 'FREQ=WEEKLY',
  BIWEEKLY: 'FREQ=WEEKLY;INTERVAL=2',
  MONTHLY: 'FREQ=MONTHLY',
  QUARTERLY: 'FREQ=MONTHLY;INTERVAL=3',
  YEARLY: 'FREQ=YEARLY',
  WEEKDAYS: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR',
  WEEKENDS: 'FREQ=WEEKLY;BYDAY=SA,SU',
  FIRST_MONDAY: 'FREQ=MONTHLY;BYDAY=MO;BYSETPOS=1',
  LAST_FRIDAY: 'FREQ=MONTHLY;BYDAY=FR;BYSETPOS=-1',
}
